import { keyframes } from '@emotion/react'
import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { MultiStyleType } from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export enum CallBlockVariants {
  Ringing = 'ringing',
  Connected = 'connected',
  OnHold = 'onHold',
}

// Define keyframes for connected status badge animation
const colorPulse = keyframes`
  from {
    background: ${asChakraVariable({ default: 'green.solid' })};
  }
  to {
    background: ${asChakraVariable({ default: 'green.emphasized' })};
  }
`

const CallBlockParts = [
  'container',
  'statusBadge',
  'number',
  'controlBar',
  'controlButton',
  'endCallButton',
  'endCallIcon',
  'muteButton',
  'muteIcon',
  'holdButton',
  'holdIcon',
  'switchButton',
  'switchIcon',
  'transferButton',
  'transferIcon',
] as const

const helpers = createMultiStyleConfigHelpers(CallBlockParts)

export const CallBlockStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      flexDirection: 'column',
      borderBottom: '1px solid',
      borderColor: 'border.default',
      gap: '8px',
      paddingY: '20px',
      alignItems: 'flex-start',
    },
    statusBadge: {
      display: 'flex',
      width: '130px',
      maxWidth: 'full',
      transition: 'width 0.25s ease-out',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '24px',
      // background removed from baseStyle - defined in variants instead
      paddingY: '4px',
      paddingX: '12px',
      ...textStyles[textStyleKeys.Body3_Bold],
    },
    number: {
      ...textStyles[textStyleKeys.Body2],
    },
    controlBar: {
      alignItems: 'flex-start',
      gap: '8px',
      paddingTop: '4px',
    },
    controlButton: {
      boxSize: '28px',
      minWidth: '0',
      borderRadius: 'full',
      background: 'transparent',
    },
    endCallButton: {
      background: 'transparent',
      _hover: {
        background: 'red.subtle',
      },
    },
    endCallIcon: {
      boxSize: '20px',
      color: 'red.solid',
    },
    muteButton: {
      background: 'transparent',
      _hover: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
      _active: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
    },
    muteIcon: {
      boxSize: '24px',
      color: 'fg.default',
    },
    holdButton: {
      background: 'transparent',
      _hover: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
      _active: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
    },
    holdIcon: {
      boxSize: '24px',
      color: 'fg.default',
    },
    switchButton: {
      background: 'transparent',
      _hover: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
      _active: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
    },
    switchIcon: {
      boxSize: '28px',
      color: 'fg.default',
    },
    transferButton: {
      background: 'transparent',
      _hover: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
      _active: {
        background: 'brand.muted',
        '& > svg': {
          color: 'brand.fg',
        },
      },
    },
    transferIcon: {
      boxSize: '24px',
      color: 'fg.default',
    },
  },
  variants: {
    [CallBlockVariants.Ringing]: {
      statusBadge: {
        background: 'border.muted',
      },
      number: {},
      endCallIcon: {},
      muteIcon: {},
      holdIcon: {},
      switchIcon: {},
    },
    [CallBlockVariants.Connected]: {
      statusBadge: {
        animation: `${colorPulse} 1s infinite alternate`,
        color: 'brand.contrast',
      },
      number: {},
      endCallIcon: {},
      muteIcon: {},
      holdIcon: {},
      switchIcon: {},
    },
    [CallBlockVariants.OnHold]: {
      statusBadge: {
        width: '72px',
        background: 'border.muted',
      },
      number: {},
      endCallIcon: {},
      muteIcon: {},
      holdIcon: {},
      switchIcon: {},
    },
  },
  defaultProps: {},
})

export const useCallBlockStyle = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(CallBlockStyle, props) as MultiStyleType<
    typeof CallBlockParts
  >
}
